/*
Case study content usage:

- Use `excerpt` for the short summary shown in the banner and card.
- Use `industry`, `challenge`, `solution`, and `result` for the quick summary in the detail page.
- Use `sections[].heading` for the normal section heading.
- Use `sections[].paragraphs` for rich content blocks. These strings support HTML.
*/
const rawCaseStudies = [
  {
    slug: "billing-optimization-optometry-practice",
    title: "Billing Optimization for Optometry Practice",
    excerpt:
      "Partnering with Physicians Success Partners (PSP), the optometry practice improved billing accuracy, reduced denials, and enhanced overall revenue cycle performance.",
    image:
      "/img/case-study-img/casestudy01/billing-optimization-optometry-practice.webp",
    date: "2026-04-04",
    metaTitle: "Billing Optimization for Optometry Practice - Physicians Success Partners",
    metaDesc:
      "Partnering with Physicians Success Partners (PSP), the optometry practice improved billing accuracy, reduced denials, and enhanced overall revenue cycle performance.",
    metaKeywords:
      "CPT coding, HCPCS coding, ICD-10 coding, medical coding accuracy, medical billing tips, revenue cycle management",

    sections: [
      {
        paragraphs: [
          "<p>A busy optometry practice with multiple providers was experiencing ongoing challenges in managing its billing operations, insurance claims, and patient collections. The practice handled a mix of routine vision exams, medical eye conditions, and specialty services, which created complexity in billing workflows.</p>",

          "<p>Frequent coding errors, confusion between vision plans and medical insurance, and delayed reimbursements were impacting both revenue and staff productivity. Front-desk staff were overwhelmed with eligibility checks, while unresolved claims continued to increase in the AR.</p>",

          "<p>To address these issues, the practice partnered with <a href='https://psprcm.us/'>Physicians Success Partners (PSP)</a> to streamline its <strong>optometry billing processes</strong>, improve claim accuracy, and strengthen overall Revenue Cycle Management (RCM) performance.</p>",

        ],
      },
      {
        heading: "Challenges",
        paragraphs: [
          "<ul><li>High rate of claim denials due to incorrect CPT/ICD coding and missing documentation</li><li>Ongoing confusion between vision insurance (routine exams) and medical insurance (eye conditions)</li><li>Delays in insurance verification, leading to claim rejections and resubmissions</li><li>Increasing Accounts Receivable (AR), with many claims aging beyond 60–90 days</li><li>Inefficient patient billing and follow-up processes, resulting in unpaid balances</li><li>Limited visibility into billing performance, making it difficult to track issues</li></ul>",

        ],
      },
      {
        heading: "Strategic Approach by Physicians Success Partners (PSP)",
        paragraphs: [
          "<img src='/img/case-study-img/casestudy01/billing-optimization-optometry-practice-subimg-01.webp' alt='Strategic Approach by Physicians Success Partners (PSP)'/>",
          `
           <ul>
             <li><strong>Optometry-Specific Billing Expertise:</strong> PSP implemented clear workflows to properly differentiate between vision and medical billing, ensuring claims were submitted to the correct payer the first time.</li> 
             <li><strong>Accurate Coding & Documentation:</strong> Review and correction of CPT and ICD coding, along with improved documentation practices to reduce errors and denials.</li> 
             <li><strong>Pre-Visit Insurance Verification:</strong> Eligibility and benefits were verified before appointments to avoid claim rejections and unexpected patient balances.</li> 
             <li><strong>End-to-End Billing Management:</strong> Complete handling of billing operations including patient data entry, charge posting, claim submission, and payment posting.</li> 
             <li><strong>Dedicated AR Follow-Up Team:</strong> Continuous follow-up on unpaid and denied claims to reduce aging AR and speed up reimbursements.</li> 
             <li><strong>Denial Management & Appeals:</strong> Structured process for identifying denial reasons and submitting timely appeals with supporting documentation.</li> 
             <li><strong>Patient Billing & Collections:</strong> Organized patient communication for outstanding balances, improving collection rates while maintaining a positive patient experience</li>
             <li><strong>Performance Reporting:</strong> Regular reports on AR status, denial trends, and collections to provide clear financial visibility.</li> 
           </ul>
          `
        ],
      },
      {
        heading: "Results",
        paragraphs: [
          `
           <ul>
             <li>Noticeable reduction in claim denials and rework</li> 
             <li>Improvement in first-pass claim acceptance rate</li> 
             <li>Faster insurance reimbursements, reducing payment delays</li> 
             <li>30%+ reduction in aging AR within the first few months</li> 
             <li>Increased patient collections and reduced outstanding balances</li> 
             <li>Improved staff efficiency, allowing the front desk to focus on patient care</li> 
           </ul>
          `
        ],
      },
      {
        heading: "Conclusion",
        paragraphs: [
          "<p>With the support of <strong>Physicians Success Partners (PSP)</strong>, the optometry practice successfully transformed its billing operations. By addressing key challenges in coding, insurance handling, and follow-ups, the practice achieved better cash flow, reduced administrative burden, and a more streamlined revenue cycle.</p>",
          "<p>Managing both vision and medical billing can be complex, but it doesn’t have to be. Partner with Physicians Success Partners(PSP) for <a href='https://psprcm.us/services/medical-billing'>specialized optometry billing solutions</a>, faster reimbursements, and improved financial performance for your practice</p> ",
        ],
      },

    ],
  },
];

export function formatCaseStudyDate(date) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date));
}

export const caseStudies = [...rawCaseStudies]
  .sort((firstCaseStudy, secondCaseStudy) => {
    const secondDate = new Date(secondCaseStudy.date).getTime();
    const firstDate = new Date(firstCaseStudy.date).getTime();
    return secondDate - firstDate;
  })
  .map((caseStudy) => ({
    ...caseStudy,
    metaTitle: caseStudy.metaTitle || caseStudy.title,
    metaDesc: caseStudy.metaDesc || caseStudy.excerpt,
    metaKeywords:
      caseStudy.metaKeywords ||
      "medical billing case study, revenue cycle management success story, PSP RCM",
    postedOn: `Posted On: ${formatCaseStudyDate(caseStudy.date)}`,
  }));

export function getCaseStudyBySlug(slug) {
  return caseStudies.find((caseStudy) => caseStudy.slug === slug);
}
